import { useState } from "react";
import { Check, Copy } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Wraps a value with a click-to-copy affordance: a clipboard icon to the left,
 * and the value as plain, selectable text. Clicking anywhere on the widget
 * copies `value`; the icon briefly swaps to a check. Hovering the icon shows a
 * tooltip with the value and "(click to copy)".
 *
 * Copying is mouse-driven only: the value stays a selectable span rather than a
 * focusable button so a drag still selects it natively, which means there is no
 * keyboard-only copy. That trade-off is acceptable for this internal tool.
 */
function CopyableValue({ value, children, className }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch {
      // Clipboard access can be denied; leave the icon unchanged.
    }
  };

  return (
    <span
      className={cn(
        "tw:inline-flex tw:max-w-full tw:items-center tw:gap-1.5 tw:cursor-pointer",
        className
      )}
      onClick={copyToClipboard}
    >
      <Tooltip>
        <TooltipTrigger
          render={<span />}
          aria-label={`${value} (click to copy)`}
          className="tw:shrink-0 tw:text-muted-foreground"
        >
          {copied ? (
            <Check className="tw:size-3.5" />
          ) : (
            <Copy className="tw:size-3.5" />
          )}
        </TooltipTrigger>
        <TooltipContent>{value} (click to copy)</TooltipContent>
      </Tooltip>
      <span className="tw:min-w-0 tw:break-words">{children ?? value}</span>
    </span>
  );
}

export default CopyableValue;
